import React from 'react';
import { tables } from '../mock';
import { useApp } from '../context/AppContext';
import { Users, Mic, FileSignature, Mail, Plus, Wine, Music2, Settings as SettingsIcon, BarChart3, Globe, Target, HandCoins } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

const iconMap = { Users, Mic, FileSignature, Mail };

const extraTables = [
  { id: 'prospects', name: 'Prospects', Icon: Target },
  { id: 'analytics', name: 'Analytics', Icon: BarChart3 },
  { id: 'mentions', name: 'Mentions', Icon: Globe },
  { id: 'contributions', name: 'Contributions', Icon: HandCoins }
];

export function SidebarContent({ onNavigate }) {
  const { shows, activeShow, activeShowId, setActiveShowId, activeTable, setActiveTable } = useApp();
  const navigate = useNavigate();
  const pick = (id) => { setActiveTable(id); onNavigate?.(); };

  return (
    <div className="h-full flex flex-col bg-[#FAF7F2]">
      <div className="px-5 py-4 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md grid place-items-center text-white font-serif" style={{ background: activeShow.color }}>
            <Wine className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-stone-900 tracking-tight">Podcast Studio</div>
            <div className="text-[11px] text-stone-500">Guest Manager</div>
          </div>
        </div>
      </div>

      <div className="px-3 py-3">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 px-2 mb-2 font-semibold">Shows</div>
        <div className="space-y-1">
          {shows.map(s => (
            <button
              key={s.id}
              data-testid={`sidebar-show-${s.id}`}
              onClick={() => { setActiveShowId(s.id); onNavigate?.(); }}
              className={cn(
                'w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left transition-colors',
                activeShowId === s.id ? 'bg-white shadow-sm border border-stone-200' : 'hover:bg-stone-100'
              )}
            >
              <span className="h-7 w-7 rounded-md grid place-items-center text-white text-[11px] font-bold shrink-0" style={{ background: s.color }}>
                {s.id === 'wine-talks' ? <Wine className="w-3.5 h-3.5" /> : <Music2 className="w-3.5 h-3.5" />}
              </span>
              <span className="text-[13px] font-medium text-stone-800 truncate">{s.name}</span>
            </button>
          ))}
          <button className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-stone-500 hover:bg-stone-100 text-[12px]">
            <Plus className="w-3.5 h-3.5" /> Add show
          </button>
        </div>
      </div>

      <div className="px-3 py-2 border-t border-stone-200">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 px-2 mb-2 font-semibold">Tables</div>
        <div className="space-y-0.5">
          {tables.map(t => {
            const Icon = iconMap[t.icon];
            const active = activeTable === t.id;
            return (
              <button
                key={t.id}
                data-testid={`sidebar-table-${t.id}`}
                onClick={() => pick(t.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left text-[13px] transition-colors',
                  active ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100'
                )}
              >
                <Icon className="w-4 h-4" />
                <span>{t.name}</span>
              </button>
            );
          })}
          {extraTables.map(t => {
            const active = activeTable === t.id;
            return (
              <button
                key={t.id}
                data-testid={`sidebar-table-${t.id}`}
                onClick={() => pick(t.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left text-[13px] transition-colors',
                  active ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100'
                )}
              >
                <t.Icon className="w-4 h-4" />
                <span>{t.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-auto p-4 border-t border-stone-200 space-y-2">
        <button data-testid="sidebar-settings-btn" onClick={() => navigate('/settings')} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-stone-700 hover:bg-stone-100 text-[13px]">
          <SettingsIcon className="w-4 h-4" /> Settings
        </button>
        <div className="rounded-lg p-3 text-white text-[12px] leading-relaxed" style={{ background: `linear-gradient(135deg, ${activeShow.color} 0%, ${activeShow.color}dd 100%)` }}>
          <div className="font-semibold mb-1">{activeShow.name}</div>
          <div className="opacity-80">{activeShow.description}</div>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside data-testid="desktop-sidebar" className="hidden md:flex w-[260px] shrink-0 h-screen border-r border-stone-200 flex-col">
      <SidebarContent />
    </aside>
  );
}
